import { useCallback, useEffect, useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { ChaveDeApi, ChaveDeApiCriada, UsoDeChaveDeApi } from '@/data/tipos';

function base(): string {
    return import.meta.env.VITE_SUPABASE_URL ?? 'https://api.formulamed.com.br';
}

function quando(iso: string | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' });
}

/**
 * As chaves de API da clínica.
 *
 * Uma credencial de máquina, estreita de propósito: somente leitura, três
 * recursos, nenhum com paciente. A proprietária gera; a chave aparece UMA vez e
 * o banco guarda só o hash. Perdeu? Revoga e gera outra.
 */
export function ChavesDeApi() {
    const aviso = useAviso();
    const proprietaria = pode('*');
    const [chaves, setChaves] = useState<ChaveDeApi[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [gerando, setGerando] = useState(false);
    const [nome, setNome] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [criada, setCriada] = useState<ChaveDeApiCriada | null>(null);
    const [revogando, setRevogando] = useState<ChaveDeApi | null>(null);
    /* Uso por dia dos últimos 30: a proprietária que desconfia de uma integração
       precisa ver QUANDO o volume mudou, não só o total desde sempre. */
    const [uso, setUso] = useState<UsoDeChaveDeApi[]>([]);
    const [usoDe, setUsoDe] = useState<ChaveDeApi | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [lidas, porDia] = await Promise.all([dados().chavesDeApi(), dados().usoDasChavesDeApi(30)]);
            setChaves(lidas);
            setUso(porDia);
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler as chaves.');
        }
    }, []);

    useEffect(() => {
        if (proprietaria) void carregar();
    }, [proprietaria, carregar]);

    const gerar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            const nova = await dados().criarChaveDeApi(nome);
            setGerando(false);
            setCriada(nova);
            await carregar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível gerar a chave.');
        } finally {
            setSalvando(false);
        }
    };

    const revogar = async () => {
        if (!revogando) return;
        setSalvando(true);
        try {
            setChaves(await dados().revogarChaveDeApi(revogando.id));
            aviso({ title: 'Chave revogada', description: `${revogando.nome} deixou de abrir a API.` });
            setRevogando(null);
        } catch (e) {
            aviso({ title: 'Não foi possível revogar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: 'Chave copiada' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: 'Selecione a chave e copie à mão.', tone: 'warning' });
        }
    };

    const colunas: DataTableColumn<ChaveDeApi>[] = [
        {
            key: 'nome', header: 'Chave',
            render: (k) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{k.nome}</strong>
                    <code style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{k.prefixo}_…</code>
                </span>
            ),
        },
        { key: 'criadaEm', header: 'Criada', width: 170, render: (k) => `${quando(k.criadaEm)}${k.autorNome ? ` · ${k.autorNome}` : ''}` },
        { key: 'ultimoUsoEm', header: 'Último uso', width: 170, render: (k) => (k.usos ? `${quando(k.ultimoUsoEm)} · ${k.usos} usos` : 'nunca usada') },
        {
            key: 'uso30', header: 'Últimos 30 dias', width: 150,
            render: (k) => {
                const total = uso.filter((u) => u.chaveId === k.id).reduce((s, u) => s + u.chamadas, 0);
                return total === 0
                    ? 'sem chamadas'
                    : <Button size="sm" variant="ghost" onClick={() => setUsoDe(k)} aria-label={`Uso por dia de ${k.nome}`}>{`${String(total)} chamadas`}</Button>;
            },
        },
        {
            key: 'revogadaEm', header: 'Situação', width: 120,
            render: (k) => (k.revogadaEm ? <Badge tone="neutral">Revogada</Badge> : <Badge tone="success" dot>Ativa</Badge>),
        },
        {
            key: 'acoes', header: '', align: 'right', width: 110,
            render: (k) => (k.revogadaEm ? null : <Button size="sm" variant="ghost" onClick={() => setRevogando(k)}>Revogar</Button>),
        },
    ];

    return (
        <Card
            title="Chaves de API"
            subtitle="Credencial de máquina da clínica: somente leitura, sem paciente, revogável"
            padding={18}
            actions={proprietaria ? (
                <Button size="sm" iconLeft={<Icon name="key-round" size={14} />} onClick={() => { setNome(''); setErro(null); setGerando(true); }}>
                    Gerar chave
                </Button>
            ) : undefined}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, lineHeight: '19px' }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                    A chave lê três coisas, e nada além: os procedimentos ativos, as unidades e os horários ocupados da
                    agenda — sem quem é o paciente. É o que um site de agendamento precisa. Mande-a no cabeçalho{' '}
                    <code>x-api-key</code>:
                </p>
                <pre style={{ margin: 0, padding: 12, overflowX: 'auto', background: 'var(--surface-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
                    {`curl -H "x-api-key: vk_…" ${base()}/functions/v1/api/v1/procedimentos\ncurl -H "x-api-key: vk_…" "${base()}/functions/v1/api/v1/agenda/ocupados?de=2026-09-01&ate=2026-09-30"`}
                </pre>

                {!proprietaria && (
                    <Alert tone="neutral" dismissible={false} title="Só a proprietária gera e revoga chaves">
                        Uma chave não pertence a ninguém e não expira: quem responde pela clínica decide quem a recebe.
                    </Alert>
                )}
                {erro && !gerando && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {proprietaria && (
                    <DataTable
                        columns={colunas} rows={chaves ?? []} rowKey="id" stickyHeader={false}
                        emptyMessage={chaves === null ? 'Lendo as chaves…' : 'Nenhuma chave gerada.'}
                    />
                )}
            </div>

            <Modal
                open={gerando} onClose={() => setGerando(false)} width={460} title="Gerar chave de API"
                description="Dê um nome que diga onde ela vai ser usada — é assim que você a reconhece para revogar."
                dirty={nome.trim().length > 0}
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => setGerando(false)}>Cancelar</Button>
                        <Button loading={salvando} disabled={nome.trim().length < 3} onClick={() => { void gerar(); }}>Gerar</Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                    <FormField label="Nome" htmlFor="chave-nome" required hint="Ex.: site de agendamento.">
                        <Input id="chave-nome" value={nome} onChange={setNome} />
                    </FormField>
                </div>
            </Modal>

            <Modal
                open={criada !== null} onClose={() => setCriada(null)} width={520} title="Copie a chave agora"
                description={criada ? criada.chave.nome : ''}
                footer={<Button onClick={() => setCriada(null)}>Já copiei</Button>}
            >
                {criada && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Alert tone="warning" dismissible={false} title="Esta é a única vez que ela aparece">
                            O banco guarda só um resumo que não volta a ser chave. Se perder, revogue esta e gere outra.
                        </Alert>
                        <code style={{ display: 'block', padding: 12, wordBreak: 'break-all', background: 'var(--surface-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                            {criada.segredo}
                        </code>
                        <Button variant="secondary" iconLeft={<Icon name="copy" size={14} />} onClick={() => { void copiar(criada.segredo); }}>Copiar</Button>
                    </div>
                )}
            </Modal>

            <Modal
                open={revogando !== null} onClose={() => setRevogando(null)} width={460} tone="danger" title="Revogar a chave?"
                description={revogando ? `${revogando.nome} (${revogando.prefixo}_…) deixa de abrir a API na hora. Não dá para desfazer.` : ''}
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => setRevogando(null)}>Cancelar</Button>
                        <Button variant="danger" loading={salvando} onClick={() => { void revogar(); }}>Revogar</Button>
                    </>
                )}
            />

            <Modal
                open={usoDe !== null} onClose={() => setUsoDe(null)} width={420} title="Uso por dia"
                description={usoDe ? `${usoDe.nome} (${usoDe.prefixo}_…) · últimos 30 dias, no horário de São Paulo` : ''}
                footer={<Button variant="secondary" onClick={() => setUsoDe(null)}>Fechar</Button>}
            >
                {usoDe && (
                    <DataTable
                        columns={[
                            { key: 'dia', header: 'Dia', render: (u: UsoDeChaveDeApi) => new Date(`${u.dia}T12:00:00-03:00`).toLocaleDateString('pt-BR') },
                            { key: 'chamadas', header: 'Chamadas', align: 'right', render: (u: UsoDeChaveDeApi) => String(u.chamadas) },
                        ]}
                        rows={uso.filter((u) => u.chaveId === usoDe.id)} rowKey="dia" stickyHeader={false}
                        emptyMessage="Nenhuma chamada nos últimos 30 dias."
                    />
                )}
            </Modal>
        </Card>
    );
}
